import { useMemo, useState, type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { AlertTriangle, ChevronRight, ListTodo, Plus } from 'lucide-react';

import {
  HoverCard,
  HoverCardContent,
  HoverCardTrigger,
} from '@/components/ui/hover-card';
import { formatDate } from '@/lib/format';
import { cn } from '@/lib/utils';
import {
  resolvedNameLabel,
  useResolveUserDisplayNames,
} from '@/features/patient-record/components/panels/CareTeamPanel/utils';
import { patientQueries } from '@/features/patient-record/queries';
import type { PatientTaskSummaryViewModel } from '@/features/patient-record/types';

import { classifyDueDate } from '../panels/TasksPanel/taskHelpers';
import { TaskCompleteButton } from '../panels/TasksPanel/TaskCompleteButton';
import { TaskCreateDialog } from '../panels/TasksPanel/TaskCreateDialog';
import { TaskEditDialog } from '../panels/TasksPanel/TaskEditDialog';

const PREVIEW_LIMIT = 5;

interface TasksChipProps {
  readonly guid: string;
}

/**
 * Header-strip tasks chip (PTV2-1097). Shows the open-task count in a
 * single control alongside Export / governance; hover / focus opens the
 * preview popover, which is also where the create-task affordance lives
 * (a `+` in its header) — keeps the identity band uncluttered by
 * avoiding a second header-level button. Preview lists up to five open
 * tasks, sorted by due date with red/amber/neutral colour coding; more
 * than five shows a "view more" link to My Cases.
 *
 * Reuses the same `patientQueries.tasks(guid, { status: 'TODO' })`
 * cache as the side panel, so mutations there propagate instantly.
 */
export function TasksChip({ guid }: TasksChipProps): ReactElement {
  const { t } = useTranslation();
  const tasksQuery = useQuery(patientQueries.tasks(guid, { status: 'TODO' }));
  const [creating, setCreating] = useState(false);
  const [previewTaskId, setPreviewTaskId] = useState<string | null>(null);

  const sorted = useMemo<readonly PatientTaskSummaryViewModel[]>(() => {
    if (!tasksQuery.isSuccess) return [];
    return sortByDueDateAscending(tasksQuery.data);
  }, [tasksQuery.isSuccess, tasksQuery.data]);

  const count = sorted.length;
  const overflow = Math.max(0, count - PREVIEW_LIMIT);
  const preview = sorted.slice(0, PREVIEW_LIMIT);

  const assigneeGuids = useMemo<readonly string[]>(
    () =>
      preview
        .map((task) => task.assigneeId)
        .filter((id): id is string => id !== null),
    [preview],
  );
  const assigneeNames = useResolveUserDisplayNames(assigneeGuids);

  return (
    <>
      <HoverCard openDelay={150} closeDelay={120}>
        <HoverCardTrigger asChild>
          <button
            type="button"
            aria-label={t('patient.tasks.chipAriaLabel', { count })}
            className="inline-flex items-center gap-1.5 h-6 px-2 rounded-full border border-border bg-card hover:bg-muted/60 text-[11px] font-semibold uppercase tracking-[0.06em] text-foreground transition-colors"
          >
            <ListTodo className="h-3 w-3" aria-hidden="true" />
            <span>{t('patient.tasks.chipLabel')}</span>
            <span
              className={cn(
                'inline-flex items-center justify-center min-w-[16px] h-4 px-1 rounded-full text-[10px] tabular-nums',
                count === 0
                  ? 'bg-muted text-muted-foreground'
                  : 'bg-primary text-primary-foreground',
              )}
            >
              {count}
            </span>
          </button>
        </HoverCardTrigger>
        <HoverCardContent
          align="end"
          sideOffset={8}
          className="w-[320px] p-0"
        >
          <header className="flex items-center justify-between gap-2 px-3 py-2 border-b border-border">
            <div className="flex items-center gap-2 min-w-0">
              <h3 className="text-[12.5px] font-semibold text-foreground truncate">
                {t('patient.tasks.previewHeading')}
              </h3>
              <DueLegend />
            </div>
            <button
              type="button"
              onClick={() => {
                setCreating(true);
              }}
              aria-label={t('patient.tasks.addTaskAriaLabel')}
              className="inline-flex items-center gap-1 rounded-md px-1.5 py-0.5 text-[11.5px] font-medium text-primary hover:bg-primary/10 focus-visible:outline-hidden focus-visible:bg-primary/10"
            >
              <Plus className="h-3 w-3" aria-hidden="true" />
              {t('patient.tasks.addTaskShort')}
            </button>
          </header>
          {tasksQuery.isPending && (
            <p className="px-3 py-3 text-[12.5px] text-muted-foreground italic">
              {t('patient.tasks.loading')}
            </p>
          )}
          {tasksQuery.isSuccess && preview.length === 0 && (
            <p className="px-3 py-3 text-[12.5px] text-muted-foreground italic">
              {t('patient.tasks.empty')}
            </p>
          )}
          {tasksQuery.isSuccess && preview.length > 0 && (
            <ul className="max-h-[280px] overflow-y-auto">
              {preview.map((task) => (
                <li key={task.id}>
                  <PreviewRow
                    guid={guid}
                    task={task}
                    // Lookup in flight → null (line hidden); lookup failed → "Unknown user".
                    assigneeName={
                      task.assigneeId !== null
                        ? resolvedNameLabel(
                            assigneeNames.get(task.assigneeId),
                            t('patient.careTeam.unknownUser'),
                          )
                        : null
                    }
                    onSelect={() => {
                      setPreviewTaskId(task.id);
                    }}
                  />
                </li>
              ))}
            </ul>
          )}
          {overflow > 0 && (
            <footer className="px-3 py-2 border-t border-border bg-muted/30">
              <Link
                to="/my-cases"
                className="inline-flex items-center gap-1 text-[12px] text-primary hover:underline"
              >
                {t('patient.tasks.viewMore', { count: overflow })}
                <ChevronRight className="h-3 w-3" aria-hidden="true" />
              </Link>
            </footer>
          )}
        </HoverCardContent>
      </HoverCard>

      {creating && (
        <TaskCreateDialog
          guid={guid}
          onClose={() => {
            setCreating(false);
          }}
        />
      )}
      {previewTaskId !== null && (
        <TaskEditDialog
          guid={guid}
          taskId={previewTaskId}
          onClose={() => {
            setPreviewTaskId(null);
          }}
        />
      )}
    </>
  );
}

interface PreviewRowProps {
  readonly guid: string;
  readonly task: PatientTaskSummaryViewModel;
  readonly assigneeName: string | null;
  readonly onSelect: () => void;
}

function PreviewRow({ guid, task, assigneeName, onSelect }: PreviewRowProps): ReactElement {
  const { t } = useTranslation();
  const due = task.dueDate;
  const status = classifyDueDate(due);
  return (
    <div className="flex w-full items-start gap-2 px-3 py-2 hover:bg-muted/40 transition-colors focus-within:bg-muted/60">
      <TaskCompleteButton
        guid={guid}
        taskId={task.id}
        className={cn(
          'mt-0.5',
          status === 'overdue'
            ? 'text-destructive'
            : status === 'soon'
              ? 'text-warning'
              : undefined,
        )}
      />
      <button
        type="button"
        onClick={onSelect}
        className="min-w-0 flex-1 text-left focus-visible:outline-hidden"
        aria-label={t('patient.tasks.openTaskAriaLabel', { title: task.title })}
      >
        <p className="text-[12.5px] font-medium text-foreground leading-tight truncate">
          {task.title}
        </p>
        <div className="mt-0.5 flex items-center gap-2 text-[11px] text-muted-foreground">
          {task.assigneeId !== null && assigneeName !== null && (
            <span className="truncate">
              {t('patient.tasks.previewAssignedTo', { name: assigneeName })}
            </span>
          )}
          {due !== null && (
            <span
              className={cn(
                'inline-flex items-center gap-0.5 tabular-nums',
                status === 'overdue' && 'text-destructive font-semibold',
                status === 'soon' && 'text-warning font-semibold',
              )}
            >
              {status === 'overdue' && (
                <AlertTriangle className="h-3 w-3" aria-hidden="true" />
              )}
              {formatDate(due)}
            </span>
          )}
        </div>
      </button>
    </div>
  );
}

function DueLegend(): ReactElement {
  const { t } = useTranslation();
  return (
    <span
      aria-label={t('patient.tasks.legendAriaLabel')}
      className="flex items-center gap-2 text-[10px] text-muted-foreground"
    >
      <span className="inline-flex items-center gap-1">
        <span aria-hidden="true" className="h-2 w-2 rounded-full bg-destructive" />
        {t('patient.tasks.legendOverdue')}
      </span>
      <span className="inline-flex items-center gap-1">
        <span aria-hidden="true" className="h-2 w-2 rounded-full bg-warning" />
        {t('patient.tasks.legendSoon')}
      </span>
    </span>
  );
}

function sortByDueDateAscending(
  tasks: readonly PatientTaskSummaryViewModel[],
): readonly PatientTaskSummaryViewModel[] {
  return [...tasks].sort((a, b) => {
    if (a.dueDate !== null && b.dueDate !== null) {
      return new Date(a.dueDate).getTime() - new Date(b.dueDate).getTime();
    }
    if (a.dueDate !== null) return -1;
    if (b.dueDate !== null) return 1;
    return 0;
  });
}
